import { createBrowserClient } from "@supabase/ssr";
import type { Database } from "@/lib/types/supabase";

/**
 * Creates a Supabase client for browser usage
 * This client is used in both authenticated and public routes
 *
 * Dual-key support:
 * - Primary: New publishable key format (sb_publishable_*)
 * - Fallback: Legacy anon key (JWT format)
 *
 * Note: We explicitly set cookie options for OAuth compatibility.
 * SameSite: "lax" is required for OAuth flows to work properly.
 */
export function createClient(): ReturnType<
	typeof createBrowserClient<Database>
> {
	// Ensure we have the required environment variables
	const url = process.env.NEXT_PUBLIC_SUPABASE_URL;

	// Try new key format first, fall back to legacy
	const key = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ||
	            process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

	if (!url || !key) {
		throw new Error(
			"Missing Supabase environment variables. Please check NEXT_PUBLIC_SUPABASE_URL and either NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY or NEXT_PUBLIC_SUPABASE_ANON_KEY"
		);
	}

	return createBrowserClient<Database>(
		url,
		key,
		{
			auth: {
				autoRefreshToken: true,
				persistSession: true,
				detectSessionInUrl: true,
			},
			cookies: {
				get(name: string) {
					if (typeof document === "undefined") {
						return undefined;
					}
					const cookies = document.cookie.split("; ");
					const cookie = cookies.find((c) => c.startsWith(`${name}=`));
					if (!cookie) return undefined;
					
					const value = cookie.split("=")[1];
					return value ? decodeURIComponent(value) : undefined;
				},
				set(name: string, value: string, options?: { maxAge?: number; expires?: Date }) {
					if (typeof document === "undefined") {
						return;
					}
					let cookieString = `${name}=${encodeURIComponent(value)}`;
					
					// Explicitly set SameSite=Lax for OAuth compatibility
					cookieString += "; SameSite=Lax";
					
					// Always set Secure in production
					if (window.location.protocol === "https:") {
						cookieString += "; Secure";
					}
					
					// Set path to root
					cookieString += "; Path=/";
					
					// Handle other options
					if (options?.maxAge) {
						cookieString += `; Max-Age=${options.maxAge}`;
					}
					if (options?.expires) {
						cookieString += `; Expires=${options.expires.toUTCString()}`;
					}
					
					document.cookie = cookieString;
				},
				remove(name: string) {
					if (typeof document === "undefined") {
						return;
					}
					let cookieString = `${name}=; Max-Age=0`;
					cookieString += "; Path=/";
					
					// Match the same SameSite and Secure settings
					cookieString += "; SameSite=Lax";
					if (window.location.protocol === "https:") {
						cookieString += "; Secure";
					}
					
					document.cookie = cookieString;
				},
			},
		},
	);
}
